import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Edit, OpenWith, Undo } from "@mui/icons-material";
import { Box, Button, Divider, Typography } from "@mui/material";

import analytics from "fond/analytics";
import { selectCurrentAccount, selectCurrentAllocation, selectCurrentSubscription } from "fond/api";
import { inputLayerGroups } from "fond/layers";
import { beginEditing, deleteLayerFeatures, EditMode, getCurrentProject, repositionHubs, revertHubs, setEditMode } from "fond/project/redux";
import { LayerIds, SubscriptionStatus } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { Actions, licenseCheck, subscriptionCheck } from "fond/utils/permissions";
import { isExtendedPlannerProject } from "fond/utils/project";
import { ConfirmModal, useStackedNavigationContext } from "fond/widgets";
import { LockIcon } from "fond/widgets/Navigation/navigation.styles";

interface IProps {
  hasEditedEquipment: boolean;
  hasDropAssignments: boolean;
}

const SolveEditing: React.FC<IProps> = ({ hasEditedEquipment, hasDropAssignments }: IProps) => {
  const { open } = useStackedNavigationContext();
  const dispatch = useAppDispatch();
  const currentAllocation = useSelector(selectCurrentAllocation);
  const currentAccount = useSelector(selectCurrentAccount);
  const currentSubscription = useSelector((state) => currentAccount && selectCurrentSubscription(state, currentAccount.ID));
  const project = useAppSelector((state) => getCurrentProject(state.project));
  const isAllowed =
    licenseCheck(currentAllocation, Actions.PLANNER_PROJECT_EDIT) &&
    subscriptionCheck(currentSubscription, Actions.PLANNER_PROJECT_EDIT) &&
    currentSubscription?.Status === SubscriptionStatus.Active;

  const [confirmModal, setConfirmModal] = useState<"reset" | "moveHubs" | null>(null);

  const onResetEquipmentClick = () => {
    analytics.track("Clicked reset moved equipment");
    dispatch(revertHubs());
    setConfirmModal(null);
  };

  const performMoveHubs = () => {
    dispatch(repositionHubs());
    setConfirmModal(null);

    open("draw");
  };

  const onMoveEquipmentClick = () => {
    analytics.track("Clicked move equipment");
    if (hasDropAssignments) {
      setConfirmModal("moveHubs");
    } else {
      performMoveHubs();
    }
  };

  const onConfirmMoveHubs = () => {
    dispatch(deleteLayerFeatures([LayerIds.dropAssignment], "move-hubs"));
    performMoveHubs();
  };

  const onEditDropAssignmentsClick = () => {
    analytics.track("Clicked edit drop assignments");
    dispatch(beginEditing(inputLayerGroups.dropAssignments.id));
    dispatch(setEditMode(EditMode.edit));
    setConfirmModal(null);

    open("draw");
  };

  return (
    <>
      {confirmModal === "reset" && (
        <ConfirmModal
          open
          content="Are you sure you want to reset all design edits?"
          onConfirm={onResetEquipmentClick}
          onCancel={() => setConfirmModal(null)}
        />
      )}
      {confirmModal === "moveHubs" && (
        <ConfirmModal
          open
          content="Drop assignments must be reset to move hubs. Are you sure you want to reset your drop assignments?"
          onConfirm={onConfirmMoveHubs}
          onCancel={() => setConfirmModal(null)}
        />
      )}
      <Box mt={2}>
        <Typography variant="h6" fontSize={14}>
          Make refinements
        </Typography>
        <Box display="flex" justifyContent="center" mt={0.5} mb={0.5}>
          {!isAllowed && <LockIcon />}
        </Box>
        <Typography variant="body3" textAlign={isAllowed ? undefined : "center"}>
          {isAllowed
            ? "The design has been successfully generated. To make modifications select an action below:"
            : "You need a license/subscription to make refinements."}
        </Typography>
        <Box my={2}>
          <Divider />
          <Button
            data-testid="reposition-hubs-button"
            fullWidth
            sx={{ my: 0.5, justifyContent: "left" }}
            startIcon={<OpenWith sx={{ height: 20, color: (theme) => theme.palette.primary.main }} />}
            onClick={onMoveEquipmentClick}
            disabled={!isAllowed}
          >
            <Typography color={(theme) => theme.palette.biarri.secondary.darkGrey} fontWeight={500} fontSize={13}>
              Move hubs
            </Typography>
          </Button>
          <Divider />
          {isExtendedPlannerProject(project) && (
            <>
              <Button
                data-testid="edit-drop-assignments-button"
                fullWidth
                sx={{ my: 0.5, justifyContent: "left" }}
                startIcon={<Edit sx={{ height: 20, color: (theme) => theme.palette.primary.main }} />}
                onClick={onEditDropAssignmentsClick}
                disabled={!isAllowed}
              >
                <Typography color={(theme) => theme.palette.biarri.secondary.darkGrey} fontWeight={500} fontSize={13}>
                  Edit drop assignments
                </Typography>
              </Button>
              <Divider />
            </>
          )}

          {hasEditedEquipment && isAllowed && (
            <>
              <Button
                data-testid="reset-equipment-button"
                fullWidth
                sx={{ my: 0.5, justifyContent: "left", color: (theme) => theme.palette.biarri.secondary.darkGrey, fontWeight: 500, fontSize: 13 }}
                startIcon={<Undo sx={{ height: 20, color: (theme) => theme.palette.primary.main }} />}
                onClick={() => setConfirmModal("reset")}
                disabled={!isAllowed}
              >
                Reset edits
              </Button>
              <Divider />
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default SolveEditing;
